<template>
  <div class="content-alumni-register">
    <div class="inner">
      <template v-if="registrationStatus !== 'success'">
        <h2 class="title text-2xl">{{ t('title') }}</h2>
        <AlumniForm
          class="form"
          :member="null"
          :send-label="t('submit')"
          :disabled="registrationStatus === 'pending'"
          :email-editable="true"
          :error="registrationStatus === 'error' ? t('error.registrationFailed') : undefined"
          use-privacy
          @submit="register($event)"
        />
      </template>
      <div v-else-if="registrationStatus === 'success'" class="confirmation">
        <template v-for="module in data.confirmation" :key="module.id">
          <h3 v-if="module.title" class="title text-2xl" v-html="module.title" />
          <StructuredText
            v-if="module.content"
            class="structured-text content text-lg"
            :data="module.content"
            :render-block="renderBlock"
          />
        </template>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n';
import { StructuredText } from 'vue-datocms';
import AlumniForm, { type AlumniFormSubmitEventData } from '~/components/content-modules/alumni/AlumniForm.vue';
import type { ContentAlumniRegisterFragment } from '#gql';
import renderBlock from '~/datocms/renderBlock';
const { t } = useI18n();

defineProps<{
  data: ContentAlumniRegisterFragment;
}>();

const registrationStatus = ref<'idle' | 'pending' | 'success' | 'error'>('idle');

async function register({
  contactDetails,
  customFields,
  privacyPolicyAccepted,
  profilePictureFormData,
}: AlumniFormSubmitEventData) {
  registrationStatus.value = 'pending';
  try {
    const registerResult = await $fetch('/api/alumni/register', {
      method: 'POST',
      body: {
        contactDetails: {
          ...contactDetails,
        },
        customFields: {
          ...customFields,
        },
        privacyPolicyAccepted,
      },
    });

    if (!registerResult.testMode && profilePictureFormData) {
      await $fetch('/api/alumni/update-profile-picture', {
        method: 'POST',
        body: profilePictureFormData,
      });
    }

    registrationStatus.value = 'success';
  } catch (e) {
    console.error(e);
    registrationStatus.value = 'error';
  }
}
</script>

<style scoped lang="scss">
.content-alumni-register {
  @include grid-layout();

  & {
    padding: var(--site-inner-padding);
    width: 100%;
    max-width: var(--site-max-width);
  }

  > .inner {
    @media (--xs), (--sm) {
      padding: var(--spacing-sm) 0;
    }

    @media (--xs) {
      grid-column: 1 / -1;
    }

    @media (--sm) {
      grid-column: 2 / 24;
    }

    @media (--md), (--lg) {
      grid-column: 5 / 21;
      padding-bottom: var(--spacing-2xl);
    }

    > .confirmation {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      gap: var(--spacing-xs);

      :deep(a) {
        color: var(--color-main-blue);
      }
    }
  }
}
</style>

<i18n lang="yaml">
de:
  title: Anmeldeformular Alumnimitgliedschaft
  submit: Absenden
  error:
    registrationFailed: Registrierung ist fehlgeschlagen. Existiert bereits ein Mitglied mit dieser E-Mail-Adresse?

en:
  title: Alumni membership application form
  submit: Submit
  error:
    registrationFailed: Registration failed. Does a member already exist with this email address?
</i18n>
